import { Component } from '@angular/core';
import * as Validation from "../../../../shared/constants/validation.constants";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators
} from "@angular/forms";
import { Subject, takeUntil } from "rxjs";
import { CommonService } from "../../../../shared/services/common.service";
import { ActivatedRoute, Router } from "@angular/router";
import { Store } from "@ngrx/store";
import * as fromRoot from "../../../../../state/app.state";
import {
  AddClientDetailsRequest,
  DeleteClientRequest,
  FetchClientDetailsRequest,
  FetchClientStatusFilterRequest,
  FetchOrganizationTypeFilterRequest,
  UpdateClientDetailsRequest
} from "../../core/client-details.actions";
import {
  clientDetails,
  clientStatusFilter,
  organizationTypeFilter
} from "../../core/client-details.selector";
import { DropdownOption } from "../../../../../models/dropdown-option.model";
import { roles, statesList } from "../../../../shared/constants/common.constants";
import {
  PhoneNumberFormatPipe
} from "../../../../shared/pipes/phone-number-format.pipe";
import {
  profile,
  selectedUserProfile
} from "../../../../shared/core/shared.selectors";
import { ProfileModel } from "../../../../../models/profile.model";
import {
  FetchProfileByUserIdRequest
} from "../../../../shared/core/shared.actions";
import { ClientDetailsService } from "../../core/client-details.service";

@Component({
  selector: 'app-client-details-screen',
  templateUrl: './client-details-screen.component.html',
  styleUrls: ['./client-details-screen.component.css']
})
export class ClientDetailsScreenComponent {
  userRole: number;
  errorMessages: any = {};
  validation = Validation;
  isEditable: boolean = false;
  clientDetails: any;
  clientId: any;
  clientStatusOptions: DropdownOption[] = [];
  organizationTypeOptions: DropdownOption[] = [];
  statesOptions: DropdownOption[] = statesList;
  loading: boolean = false;
  errorMessageMap = {
    clientName: {
      required: 'This field is required.',
      spaces: 'Cant start / end with spaces.',
      invalid: 'Only [( ) - / :] symbols allowed.',
      minlength: 'Minimum 2 characters required',
    },
    clientStatus: {
      required: 'This field is required.'
    },
    organizationType: {
      required: 'This field is required.'
    },
    addressLine1: {
      required: 'This field is required.',
      spaces: 'Cant start / end with spaces.',
      minAlphabets: 'Minimum 2 alphabets required.',
      invalid: 'Only [, _ / -] symbols allowed',
      minlength: 'Minimum 5 characters required',
      maxlength: 'Maximum 250 characters accepted'
    },
    city: {
      required: 'Required.',
      pattern: 'Only alphabets allowed',
      minlength: 'Minimum 2 characters required',
      maxlength: 'Maximum 100 characters accepted'
    },
    state: {
      required: 'Required.'
    },
    zipCode: {
      required: 'Required.',
      minlength: 'Minimum 5 characters required',
      maxlength: 'Maximum 10 characters accepted'
    },
    phoneNumber: {
      required: 'This field is required.',
      minlength: 'Minimum 10 characters required',
      maxlength: 'Maximum 12 characters accepted'
    },
    email: {
      required: 'This field is required.',
      pattern: 'Invalid Email Id'
    },
    notes: {}
  }
  form = new FormGroup({
    clientName: new FormControl({ value: '', disabled: false }, {
      validators: [
        Validators.required,
        Validators.minLength(this.validation.client.name.length.min),
        this.organizationNameValidator()
      ],
    }),
    clientStatus: new FormControl({ value: 4, disabled: false }, {
      validators: [
        Validators.required
      ]
    }),
    organizationType: new FormControl({ value: null, disabled: false }, {
      validators: [
        Validators.required
      ]
    }),
    addressLine1: new FormControl({ value: '', disabled: false }, {
      validators: [
        Validators.required,
        Validators.minLength(this.validation.address.addressLine1.length.min),
        this.addressValidator()
      ]
    }),
    city: new FormControl({ value: '', disabled: false }, {
      validators: [
        Validators.required,
        Validators.pattern(this.validation.address.city.regex),
        Validators.minLength(this.validation.address.city.length.min),
        Validators.maxLength(this.validation.address.city.length.max),
      ]
    }),
    state: new FormControl({ value: '', disabled: false }, {
      validators: [
        Validators.required
      ]
    }),
    zipCode: new FormControl({ value: '', disabled: false }, {
      validators: [
        Validators.required,
        Validators.minLength(this.validation.address.zipCode.length.min),
        Validators.maxLength(this.validation.address.zipCode.length.max),
      ]
    }),
    phoneNumber: new FormControl({ value: '', disabled: false }, {
      validators: [
        Validators.required,
        Validators.minLength(this.validation.phoneNumber.length.min),
        Validators.maxLength(this.validation.phoneNumber.length.max),
      ]
    }),
    email: new FormControl({ value: '', disabled: false }, {
      validators: [
        Validators.required,
        Validators.pattern(this.validation.email.regex)
      ]
    }),
    notes: new FormControl({ value: '', disabled: false }),
    useSystemMailClient: new FormControl({ value: true, disabled: false })
  });
  profile: ProfileModel;
  selectedUserProfile: ProfileModel;
  roles = roles;
  useSystemMailClient: boolean = true;
  saveButtonText = 'Save';


  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private commonService: CommonService,
    private clientDetailsService: ClientDetailsService,
    private route: ActivatedRoute,
    private router: Router,
    private store: Store<fromRoot.State>,
    private phoneNumberFormatPipe: PhoneNumberFormatPipe
  ) {
    this.store.dispatch(FetchClientStatusFilterRequest());
    this.store.dispatch(FetchOrganizationTypeFilterRequest());
    this.clientId = this.route.snapshot.paramMap.get("clientId");
  }

  ngOnInit() {

    this.clientDetailsService.enableAddClientForm
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data) {
          this.form.enable();
          this.isEditable = true;
        }
      });


    this.store
      .select(profile)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        this.profile = data;
        if (data) {
          this.userRole = data.role
          if (data.role && data.role !== roles.superAdmin) {
            this.clientId = data['clientId']
          }

          if (this.clientId) {
            this.form.disable();
            const payload = {
              clientId: this.clientId,
            };

            this.store.dispatch(FetchClientDetailsRequest({ payload }));
          } else {
            this.form.enable();
            this.isEditable = true;
          }

        }
      });

    this.store
      .select(selectedUserProfile)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data) => {
        this.selectedUserProfile = data.profile;

        if (this.selectedUserProfile) {
          this.userRole = this.selectedUserProfile.role;
          if (this.selectedUserProfile?.role && this.selectedUserProfile.role !== roles.superAdmin) {
            this.clientId = this.selectedUserProfile['clientId'];
          } else {
            return;
          }

          if (this.clientId) {
            this.form.disable();
            const payload = {
              clientId: this.clientId,
            };

            this.store.dispatch(FetchClientDetailsRequest({ payload }));
          } else {
            this.form.enable();
            this.isEditable = true;
          }
        } else {
          if (this.profile.role === roles.superAdmin && !data.loading) {
            const params: any = this.route.snapshot.params;
            let userId = params?.clientAdminId ||
              params?.coachId ||
              params?.memberId;

            if (userId) {
              this.store.dispatch(FetchProfileByUserIdRequest({
                payload: { userId }
              }))
            }
          }
        }
      });


    this.store
      .select(clientDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.loading = data?.loading;

        if (this.loading) {
          this.form.disable({ emitEvent: false });
        }

        if (!data || !data.details) {
          return;
        }
        this.clientDetails = data?.details;
        this.useSystemMailClient = this.clientDetails?.useSystemMailClient;
        if (this.clientId) {
          this.form.patchValue({
            clientName: this.clientDetails?.name,
            clientStatus: this.clientDetails?.status?.key,
            organizationType: this.clientDetails?.orgType.map(item => item?.key),
            addressLine1: this.clientDetails?.address?.addressLine1,
            city: this.clientDetails?.address?.city,
            state: this.clientDetails?.address?.state,
            zipCode: this.clientDetails?.address?.zip,
            phoneNumber: this.phoneNumberFormatPipe.transform(this.clientDetails?.phoneNumber),
            email: this.clientDetails?.email,
            notes: this.clientDetails?.notes,
            useSystemMailClient: this.clientDetails.useSystemMailClient
          }, { emitEvent: false });
        }
      });

    this.store
      .select(clientStatusFilter)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.clientStatusOptions = data?.list || [];
      });

    this.store
      .select(organizationTypeFilter)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.organizationTypeOptions = data?.list || [];
      });

    this.commonService
      .onNavigationEnd
      .pipe(takeUntil(this.onDestroy))
      .subscribe(d => {
        const urlParams = {
          params: this.route.snapshot.params,
          queryParams: this.route.snapshot.queryParams
        }

        const prevUrlParams = this.commonService.urlParams.getValue();
        
        if (d.isRouteChanged || JSON.stringify(prevUrlParams) !== JSON.stringify(urlParams)) {
         this.commonService.urlParams.next(urlParams);
        }
      });

    this.form.controls.clientStatus
      .valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if ([1, 4].includes(data)) {
          this.saveButtonText = 'Save';
        } else {
          this.saveButtonText = 'Save and Invite admin';
        }
      })
  }

  save() {
    // ERROR CHECK IN THE FORM//
    if (this.form.invalid) {
      this.checkForErrors();
      setTimeout(() => {
        $(".form-field.ng-invalid")[0].parentElement.scrollIntoView();
      }, 0);
      return;
    }

    // EXTRACT VALUE FROM FORM//
    const {
      clientName,
      clientStatus,
      organizationType,
      addressLine1,
      city,
      state,
      zipCode,
      email,
      notes,
      useSystemMailClient
    } = this.form.getRawValue();

    // PAYLOAD CREATION//
    const payload = {
      name: clientName,
      status: clientStatus,
      orgType: organizationType,
      address: {
        addressLine1,
        city,
        state,
        zip: zipCode != null ? String(zipCode).trim() : zipCode,
      },
      phoneNumber: this.phoneNumberFormatPipe.cleanPhoneNumber(this.form.controls.phoneNumber.getRawValue()),
      email,
      notes,
      useSystemMailClient
    };

    // ACTION DISPATCH

    if (!notes) {
      delete payload.notes;
    }

    if (this.clientId) {
     /*  if (this.profile.role != roles.superAdmin) {
        delete payload.name;
      } */
      this.store.dispatch(
        UpdateClientDetailsRequest({
          payload: { ...payload, clientId: this.clientId },
        }),
      );
      this.form.disable();
      this.isEditable = false;
    } else {
      this.store.dispatch(AddClientDetailsRequest({ payload }));
    }
  }

  edit() {
    this.isEditable = true;
    this.form.enable();
   /*  if (this.profile.role != roles.superAdmin) {
      this.form.controls['clientName'].disable();
    } */
  }

  checkForErrors(currentField?: string) {
    this.errorMessages = {
      ...this.errorMessages,
      ...(this.commonService.checkFormValidation(this.form, this.errorMessageMap, currentField))
    };
  }

  organizationNameValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      if (new RegExp(/^\s|\s$/).test(value)) {
        return {
          spaces: true
        }
      }

      if (new RegExp(/[~`!#$%^&*+={}\\|;"'<>?]$/).test(value)) {
        return {
          invalid: true
        }
      }
      return null;
    }
  }

  addressValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      if (new RegExp(/^\s|\s$/).test(value)) {
        return {
          spaces: true
        }
      }

      if (!new RegExp(/^(?=(.*[a-zA-Z]){2})[^ ]/).test(value)) {
        return {
          minAlphabets: true
        }
      }

      if (new RegExp(/[~`!#$%^&*_+={}\\|;"'<>?/]$/).test(value)) {
        return {
          invalid: true
        }
      }
      return null;
    }
  }

  toggleButtonClicked(value) {
    this.form.patchValue(
      { useSystemMailClient: !!value }
    );
    this.form.markAsDirty();
  }

  delete() {
    this.commonService.openConfirmationDialog({
      data: {
        message: 'Are you sure you want to continue with delete?',
        submitText: 'Yes',
        classList: 'w-[40vw]',
        handleSubmit: () => {
          let payload = {
            clientId: this.clientId
          }
          this.store.dispatch(DeleteClientRequest({ payload }));
        },
        cancelText: 'No',
        handleCancel: () => {
          this.commonService.closeDialog();
        }
      }
    });
  }


  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
